(function () {
  "use strict";

  const cartDrawer = document.querySelector("[data-cart-drawer]");
  const cartContent = document.querySelector("[data-cart-drawer-content]");
  const cartCount = document.querySelector("[data-cart-count]");
  const cartPanel = cartDrawer?.querySelector(".cart-drawer__panel");
  const mobileMenu = document.querySelector("[data-mobile-menu]");
  const mobileMenuPanel = mobileMenu?.querySelector(".mobile-menu__panel");

  const CART_DRAWER_SECTION_ID = "cart-drawer";
  let cartBusy = false;
  let cartQueue = Promise.resolve();
  let activeOverlay = null;
  let lastFocused = null;

  /* Below this width the drawer is skipped entirely: the cart page is the cart. */
  const cartPageOnly = window.matchMedia("(max-width: 767px)");
  const cartUrl = `${window.Shopify?.routes?.root || "/"}cart`;
  let isNavigatingToCart = false;

  function goToCartPage() {
    isNavigatingToCart = true;
    window.location.href = cartUrl;
  }

  function focusEl(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }

  function getFocusable(container) {
    if (!container) return [];
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.hidden && el.offsetParent !== null);
  }

  function trapFocus(container, event) {
    const focusable = getFocusable(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openOverlay({ root, panel, onClose, initialFocus }) {
    if (!root) return;
    if (activeOverlay) {
      if (activeOverlay.root === root) return;
      closeOverlay();
    }
    activeOverlay = { root, panel, onClose };
    lastFocused = document.activeElement;
    root.classList.add("is-open");
    root.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    focusEl(initialFocus || getFocusable(panel)[0]);
  }

  function closeOverlay() {
    if (!activeOverlay) return;
    const { root, onClose } = activeOverlay;
    root.classList.remove("is-open");
    root.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    if (root === mobileMenu) {
      document
        .querySelectorAll(".mobile-menu__item--has-children.is-open, .mobile-menu__subitem--has-children.is-open")
        .forEach((item) => item.classList.remove("is-open"));
      mobileMenu.querySelectorAll(".mobile-menu__subnav, .mobile-menu__subnav-group").forEach((panel) => {
        panel.hidden = true;
      });
      mobileMenu.querySelectorAll("[data-mobile-submenu-toggle]").forEach((toggle) => {
        toggle.setAttribute("aria-expanded", "false");
      });
    }
    activeOverlay = null;
    if (typeof onClose === "function") onClose();
    if (lastFocused && typeof lastFocused.focus === "function") {
      focusEl(lastFocused);
    }
    lastFocused = null;
  }

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (activeOverlay) {
      e.preventDefault();
      closeOverlay();
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key !== "Tab" || !activeOverlay?.panel) return;
    trapFocus(activeOverlay.panel, e);
  });

  function formatMoney(cents, format) {
    if (!format) return String(cents / 100);
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(cents / 100));
    const parts = amount.split(".");
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts.join(","))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  function showCartError(message) {
    const el = document.querySelector("[data-cart-error]");
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function openCart() {
    openOverlay({
      root: cartDrawer,
      panel: cartPanel,
      initialFocus: cartPanel?.querySelector("[data-cart-close]"),
    });
  }

  function closeCart() {
    if (activeOverlay?.root === cartDrawer) closeOverlay();
  }

  function updateCartCount(count) {
    if (!cartCount) return;
    cartCount.textContent = count;
    if (count > 0) {
      cartCount.removeAttribute("hidden");
    } else {
      cartCount.setAttribute("hidden", "");
    }
  }

  function setCartDrawerUpdating(updating) {
    cartContent?.classList.toggle("is-updating", Boolean(updating));
  }

  function applyCartDrawerHtml(html) {
    if (!cartContent || !html) return false;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const inner = doc.querySelector("[data-cart-drawer-content]");
    if (!inner) return false;
    cartContent.innerHTML = inner.innerHTML;
    if (inner.dataset.cartItemCount != null) {
      cartContent.dataset.cartItemCount = inner.dataset.cartItemCount;
    }
    bindCartQtyButtons();
    return true;
  }

  function resolveItemCount(data) {
    if (typeof data?.item_count === "number") return data.item_count;
    const fromDrawer = cartContent?.dataset?.cartItemCount;
    if (fromDrawer != null && fromDrawer !== "") {
      const parsed = parseInt(fromDrawer, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    let count = 0;
    document.querySelectorAll("[data-cart-item] .cart-drawer__qty span").forEach((el) => {
      count += parseInt(el.textContent, 10) || 0;
    });
    return count;
  }

  function applyCartResponse(data) {
    const html = data?.sections?.[CART_DRAWER_SECTION_ID];
    if (html) applyCartDrawerHtml(html);
    updateCartCount(resolveItemCount(data));
  }

  async function refreshCartDrawer() {
    if (!cartContent) return;
    const root = window.Shopify?.routes?.root || "/";
    const res = await fetch(`${root}cart?section_id=${CART_DRAWER_SECTION_ID}`);
    if (!res.ok) return;
    const html = await res.text();
    applyCartDrawerHtml(html);
    updateCartCount(resolveItemCount({}));
  }

  function enqueueCartChange(body) {
    cartQueue = cartQueue
      .then(async () => {
        if (cartBusy) return;
        cartBusy = true;
        setCartDrawerUpdating(true);
        document.querySelectorAll("[data-cart-qty-minus], [data-cart-qty-plus], [data-cart-remove]").forEach((btn) => {
          btn.disabled = true;
        });
        try {
          const res = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...body,
              sections: CART_DRAWER_SECTION_ID,
              sections_url: "/cart",
            }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || "Cart update failed");
          }
          const cart = await res.json();
          if (cart.sections?.[CART_DRAWER_SECTION_ID]) {
            applyCartResponse(cart);
          } else {
            updateCartCount(cart.item_count);
            await refreshCartDrawer();
          }
          return true;
        } finally {
          cartBusy = false;
          setCartDrawerUpdating(false);
          document.querySelectorAll("[data-cart-qty-minus], [data-cart-qty-plus], [data-cart-remove]").forEach((btn) => {
            btn.disabled = false;
          });
        }
      })
      .catch((err) => {
        showCartError(err.message);
        console.error(err);
        return false;
      });
    return cartQueue;
  }

  function bindCartQtyButtons() {
    document.querySelectorAll("[data-cart-item]").forEach((row) => {
      const key = row.dataset.key;
      if (!key) return;
      const qtyEl = row.querySelector(".cart-drawer__qty span");
      const minus = row.querySelector("[data-cart-qty-minus]");
      const plus = row.querySelector("[data-cart-qty-plus]");
      const remove = row.querySelector("[data-cart-remove]");

      if (minus && plus && qtyEl) {
        minus.onclick = () => {
          const q = Math.max(0, parseInt(qtyEl.textContent, 10) - 1);
          enqueueCartChange({ id: key, quantity: q });
        };
        plus.onclick = () => {
          const q = parseInt(qtyEl.textContent, 10) + 1;
          enqueueCartChange({ id: key, quantity: q });
        };
      }

      if (remove) {
        remove.onclick = () => enqueueCartChange({ id: key, quantity: 0 });
      }
    });
  }

  document.querySelectorAll("[data-cart-open]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      if (cartPageOnly.matches) return;
      e.preventDefault();
      openCart();
    });
  });
  document.querySelectorAll("[data-cart-close]").forEach((btn) => {
    btn.addEventListener("click", closeCart);
  });

  bindCartQtyButtons();

  /* Cart upsell / quick-add — one-click add from drawer, pairs, or cart page */
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-upsell-add]");
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;

    const variantId = parseInt(btn.dataset.upsellAdd, 10);
    if (!variantId) return;

    const errorEl = document.querySelector("[data-cart-error]");
    if (errorEl) errorEl.hidden = true;
    btn.disabled = true;

    const onCartPage = Boolean(document.querySelector(".cart-page"));
    const redirectToCart = !onCartPage && cartPageOnly.matches;
    if (!onCartPage && !redirectToCart) {
      const current = parseInt(cartCount?.textContent || "0", 10) || 0;
      updateCartCount(current + 1);
      openCart();
      setCartDrawerUpdating(true);
    }

    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: CART_DRAWER_SECTION_ID,
          sections_url: "/cart",
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || "Could not add to cart");
      }
      const data = await res.json();

      if (onCartPage) {
        window.location.reload();
        return;
      }
      if (redirectToCart) {
        goToCartPage();
        return;
      }

      if (data.sections?.[CART_DRAWER_SECTION_ID]) {
        applyCartResponse(data);
      } else {
        await refreshCartDrawer();
      }
      btn.disabled = false;
    } catch (err) {
      showCartError(err.message);
      console.error(err);
      btn.disabled = false;
      await refreshCartDrawer().catch(() => {});
    } finally {
      setCartDrawerUpdating(false);
    }
  });

  /* Cart page: reserve room for the summary once it docks to the bottom on mobile */
  const cartSummary = document.querySelector("[data-cart-summary]");
  if (cartSummary) {
    const syncCartSummaryHeight = () => {
      const docked = cartPageOnly.matches;
      document.documentElement.style.setProperty(
        "--cart-summary-height",
        docked ? `${cartSummary.offsetHeight}px` : "0px",
      );
    };
    syncCartSummaryHeight();
    if ("ResizeObserver" in window) {
      new ResizeObserver(syncCartSummaryHeight).observe(cartSummary);
    }
    window.addEventListener("resize", syncCartSummaryHeight);
  }

  function updateCartPageQuantity(key, quantity) {
    return enqueueCartChange({ id: key, quantity }).then((ok) => {
      if (ok) window.location.reload();
    });
  }

  document.querySelectorAll("[data-cart-page-item]").forEach((row) => {
    const key = row.dataset.key;
    const wrap = row.querySelector(".cart-page__qty");
    const input = wrap?.querySelector("[data-cart-qty-input]");
    const minus = wrap?.querySelector("[data-cart-qty-minus]");
    const plus = wrap?.querySelector("[data-cart-qty-plus]");
    if (!key || !input || !minus || !plus) return;

    const commit = (quantity) => {
      const next = Math.max(0, quantity);
      input.value = String(next);
      updateCartPageQuantity(key, next);
    };

    minus.addEventListener("click", () => {
      commit((parseInt(input.value, 10) || 0) - 1);
    });
    plus.addEventListener("click", () => {
      commit((parseInt(input.value, 10) || 0) + 1);
    });
    input.addEventListener("change", () => {
      commit(parseInt(input.value, 10) || 0);
    });
  });

  /* Mobile menu */
  document.querySelectorAll("[data-mobile-menu-open]").forEach((btn) => {
    btn.addEventListener("click", () => {
      openOverlay({
        root: mobileMenu,
        panel: mobileMenuPanel,
        initialFocus: mobileMenuPanel?.querySelector("[data-mobile-menu-close]"),
      });
    });
  });
  document.querySelectorAll("[data-mobile-menu-close]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (activeOverlay?.root === mobileMenu) closeOverlay();
    });
  });
  mobileMenu?.addEventListener("click", (e) => {
    if (e.target === mobileMenu && activeOverlay?.root === mobileMenu) {
      closeOverlay();
    }
  });

  function closeDesktopNavDropdowns(exceptItem) {
    document.querySelectorAll(".site-header__nav-item--has-children.is-open").forEach((item) => {
      if (item === exceptItem) return;
      item.classList.remove("is-open");
      const toggle = item.querySelector(".site-header__nav-toggle");
      if (toggle) toggle.setAttribute("aria-expanded", "false");
    });
  }

  document.querySelectorAll(".site-header__nav-item--has-children").forEach((item) => {
    const toggle = item.querySelector(".site-header__nav-toggle");
    if (!toggle) return;

    toggle.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = item.classList.toggle("is-open");
      closeDesktopNavDropdowns(isOpen ? item : null);
      toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });
  });

  document.addEventListener("click", (e) => {
    if (e.target.closest(".site-header__nav-item--has-children")) return;
    closeDesktopNavDropdowns();
  });

  document.querySelectorAll("[data-mobile-submenu-toggle]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const item = btn.closest(".mobile-menu__item--has-children, .mobile-menu__subitem--has-children");
      const panelId = btn.getAttribute("aria-controls");
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!item || !panel) return;

      const isOpen = item.classList.toggle("is-open");
      panel.hidden = !isOpen;
      btn.setAttribute("aria-expanded", isOpen ? "true" : "false");
      const label = btn.closest(".mobile-menu__row, .mobile-menu__subrow")
        ?.querySelector(".mobile-menu__link, .mobile-menu__subnav-link")
        ?.textContent?.trim() || "submenu";
      btn.querySelector(".visually-hidden").textContent = isOpen
        ? `Hide ${label} submenu`
        : `Show ${label} submenu`;
    });
  });

  /* Product gallery */
  const galleryRoot = document.querySelector("[data-product-gallery]");
  const galleryFilterColours = galleryRoot?.hasAttribute("data-gallery-filter-colours");
  const galleryMainImage = document.querySelector("[data-gallery-main-image]");
  const galleryMainVideo = document.querySelector("[data-gallery-main-video]");
  const stickyThumb = document.querySelector("[data-sticky-thumb]");
  const productJsonEl = document.querySelector("[data-product-json]");
  let galleryMediaById = new Map();
  try {
    const parsedProduct = productJsonEl ? JSON.parse(productJsonEl.textContent) : null;
    const mediaList = Array.isArray(parsedProduct?.media) ? parsedProduct.media : [];
    galleryMediaById = new Map(mediaList.map((m) => [String(m.id), m]));
  } catch {
    galleryMediaById = new Map();
  }

  function pauseGalleryVideo() {
    if (!galleryMainVideo) return;
    galleryMainVideo.pause();
    try {
      galleryMainVideo.currentTime = 0;
    } catch {
      /* ignore seek errors before metadata */
    }
  }

  function showGalleryImage(src, srcset, sizes) {
    if (!galleryMainImage || !src) return;
    pauseGalleryVideo();
    if (galleryMainVideo) {
      galleryMainVideo.hidden = true;
      galleryMainVideo.removeAttribute("poster");
      galleryMainVideo.replaceChildren();
    }
    galleryMainImage.hidden = false;
    galleryMainImage.src = src;
    if (srcset) {
      galleryMainImage.srcset = srcset;
    } else {
      galleryMainImage.removeAttribute("srcset");
    }
    if (sizes) {
      galleryMainImage.sizes = sizes;
    } else {
      galleryMainImage.removeAttribute("sizes");
    }
  }

  function showGalleryVideo(sources, poster) {
    if (!galleryMainVideo || !Array.isArray(sources) || !sources.length) return;
    pauseGalleryVideo();
    if (galleryMainImage) galleryMainImage.hidden = true;
    galleryMainVideo.replaceChildren();
    for (const source of sources) {
      if (!source?.src) continue;
      const el = document.createElement("source");
      el.src = source.src;
      if (source.type) el.type = source.type;
      galleryMainVideo.appendChild(el);
    }
    if (poster) {
      galleryMainVideo.poster = poster;
    } else {
      galleryMainVideo.removeAttribute("poster");
    }
    // Muted autoplay is required by browser policy; shoppers can unmute via controls.
    galleryMainVideo.muted = true;
    galleryMainVideo.defaultMuted = true;
    galleryMainVideo.setAttribute("muted", "");
    galleryMainVideo.autoplay = true;
    galleryMainVideo.loop = true;
    galleryMainVideo.hidden = false;
    galleryMainVideo.load();
    const playAttempt = galleryMainVideo.play();
    if (playAttempt?.catch) playAttempt.catch(() => {});
  }

  function applyMainMedia(src, srcset, sizes, mediaType, sources) {
    if (mediaType === "video" && sources?.length) {
      showGalleryVideo(sources, src);
      return;
    }
    showGalleryImage(src, srcset, sizes);
  }

  function mediaFromThumb(thumb) {
    if (!thumb) return null;
    const fromJson = galleryMediaById.get(String(thumb.dataset.mediaId));
    if (fromJson) return fromJson;
    return {
      media_type: thumb.dataset.mediaType || "image",
      src: thumb.dataset.src,
      srcset: thumb.dataset.srcset,
      sources: null,
    };
  }

  function selectGalleryThumb(thumb) {
    if (!thumb) return;
    const media = mediaFromThumb(thumb);
    applyMainMedia(
      media?.src || thumb.dataset.src,
      media?.srcset || thumb.dataset.srcset,
      thumb.dataset.sizes,
      media?.media_type || thumb.dataset.mediaType,
      media?.sources,
    );
    document.querySelectorAll("[data-gallery-thumb]").forEach((t) => t.classList.remove("is-active"));
    thumb.classList.add("is-active");
    thumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }

  function visibleGalleryThumbs() {
    return Array.from(document.querySelectorAll("[data-gallery-thumb]")).filter((thumb) => {
      const item = thumb.closest("[data-gallery-thumb-item]");
      return !item?.hidden;
    });
  }

  const galleryPrevBtn = document.querySelector("[data-gallery-prev]");
  const galleryNextBtn = document.querySelector("[data-gallery-next]");

  function updateGalleryNav() {
    const show = visibleGalleryThumbs().length > 1;
    if (galleryPrevBtn) galleryPrevBtn.hidden = !show;
    if (galleryNextBtn) galleryNextBtn.hidden = !show;
  }

  function stepGallery(delta) {
    const thumbs = visibleGalleryThumbs();
    if (thumbs.length < 2) return;
    const activeIndex = thumbs.findIndex((thumb) => thumb.classList.contains("is-active"));
    const from = activeIndex >= 0 ? activeIndex : 0;
    const nextIndex = (from + delta + thumbs.length) % thumbs.length;
    selectGalleryThumb(thumbs[nextIndex]);
  }

  galleryPrevBtn?.addEventListener("click", () => stepGallery(-1));
  galleryNextBtn?.addEventListener("click", () => stepGallery(1));
  updateGalleryNav();

  const galleryThumbs = document.querySelector("[data-gallery-thumbs]");

  if (galleryThumbs) {
    let dragPointerId = null;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let dragMoved = false;
    const dragThreshold = 6;

    galleryThumbs.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "mouse" || event.button !== 0) return;
      dragPointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartScrollLeft = galleryThumbs.scrollLeft;
      dragMoved = false;
    });

    galleryThumbs.addEventListener("pointermove", (event) => {
      if (dragPointerId !== event.pointerId) return;
      const deltaX = event.clientX - dragStartX;
      if (!dragMoved && Math.abs(deltaX) < dragThreshold) return;
      // Capture only after a real drag so plain clicks still reach the thumb buttons.
      if (!dragMoved) {
        dragMoved = true;
        galleryThumbs.classList.add("is-dragging");
        try {
          galleryThumbs.setPointerCapture(event.pointerId);
        } catch {
          /* ignore capture errors */
        }
      }
      galleryThumbs.scrollLeft = dragStartScrollLeft - deltaX;
      event.preventDefault();
    });

    function endGalleryThumbsDrag(event) {
      if (dragPointerId !== event.pointerId) return;
      const didDrag = dragMoved;
      dragPointerId = null;
      galleryThumbs.classList.remove("is-dragging");
      dragMoved = false;
      if (!didDrag) return;
      const suppressClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        galleryThumbs.removeEventListener("click", suppressClick, true);
      };
      galleryThumbs.addEventListener("click", suppressClick, true);
    }

    galleryThumbs.addEventListener("pointerup", endGalleryThumbsDrag);
    galleryThumbs.addEventListener("pointercancel", endGalleryThumbsDrag);
    galleryThumbs.addEventListener("dragstart", (event) => event.preventDefault());

    galleryThumbs.addEventListener("click", (event) => {
      const thumb = event.target.closest("[data-gallery-thumb]");
      if (!thumb || !galleryThumbs.contains(thumb)) return;
      selectGalleryThumb(thumb);
    });
  } else {
    document.querySelectorAll("[data-gallery-thumb]").forEach((thumb) => {
      thumb.addEventListener("click", () => selectGalleryThumb(thumb));
    });
  }

  function filterGalleryByColour(colour) {
    if (!galleryFilterColours) return;
    const items = document.querySelectorAll("[data-gallery-thumb-item]");
    if (!items.length) return;

    let anyColoured = false;
    items.forEach((item) => {
      const thumb = item.querySelector("[data-gallery-thumb]");
      const thumbColour = thumb?.dataset.colour || "";
      if (thumbColour) anyColoured = true;
    });
    if (!anyColoured || !colour) {
      items.forEach((item) => {
        item.hidden = false;
      });
      updateGalleryNav();
      return;
    }

    let visible = 0;
    items.forEach((item) => {
      const thumb = item.querySelector("[data-gallery-thumb]");
      const thumbColour = thumb?.dataset.colour || "";
      const show = !thumbColour || thumbColour === colour;
      item.hidden = !show;
      if (show) visible += 1;
    });

    if (visible === 0) {
      items.forEach((item) => {
        item.hidden = false;
      });
    }
    updateGalleryNav();
  }

  function selectGalleryMedia(mediaId, mediaList) {
    if (!mediaId) return;
    const thumb = document.querySelector(
      `[data-gallery-thumb][data-media-id="${mediaId}"]`,
    );
    if (thumb) {
      const item = thumb.closest("[data-gallery-thumb-item]");
      if (item) item.hidden = false;
      selectGalleryThumb(thumb);
      return;
    }
    const media = (mediaList || []).find((m) => m.id === mediaId);
    if (media) {
      applyMainMedia(
        media.src,
        media.srcset,
        "(min-width: 64rem) 50vw, 100vw",
        media.media_type,
        media.sources,
      );
    }
  }

  /* Variant selector */
  const variantInput = document.querySelector("[data-variant-id]");
  const addBtn = document.querySelector("[data-add-to-cart]");
  const productForm = document.querySelector("[data-product-form]");
  const stickyBar = document.querySelector("[data-sticky-atc]");
  const stickySelect = stickyBar?.querySelector("[data-sticky-variant]");
  const stickyPriceEl = stickyBar?.querySelector("[data-sticky-price]");
  const stickyAddBtn = stickyBar?.querySelector("[data-sticky-add]");

  if (productJsonEl && variantInput) {
    const product = JSON.parse(productJsonEl.textContent);
    const productPage = document.querySelector(".product-page");
    const moneyFormat = productPage?.dataset.moneyFormat || "{{amount}}";
    const labelAdd = productPage?.dataset.labelAdd || "Add to cart";
    const labelSoldOut = productPage?.dataset.labelSoldOut || "Sold out";
    const optionButtons = document.querySelectorAll("[data-option-value]");
    const stockStatusEl = document.querySelector("[data-stock-status]");
    const inStockText = stockStatusEl?.dataset.inStockText || "In stock";
    const colourOptionIndex =
      typeof product.colour_option_index === "number" ? product.colour_option_index : null;
    const productMedia = Array.isArray(product.media) ? product.media : [];

    function getSelectedOptions() {
      const selected = [];
      document.querySelectorAll(".variant-selector__group").forEach((group, i) => {
        const active = group.querySelector(".variant-selector__option.is-active");
        if (active) {
          selected[i] = active.dataset.optionValue;
        } else {
          const firstVariant = product.variants.find((v) => v.available) || product.variants[0];
          selected[i] = firstVariant?.options[i] || "";
        }
      });
      return selected;
    }

    function findVariant() {
      const groups = document.querySelectorAll(".variant-selector__group");
      if (!groups.length) {
        return product.variants.find((v) => v.available) || product.variants[0];
      }
      const selected = getSelectedOptions();
      return (
        product.variants.find((v) => v.options.every((opt, i) => opt === selected[i])) ||
        product.variants.find((v) => v.available) ||
        product.variants[0]
      );
    }

    function findVariantFromUrl() {
      const id = new URL(window.location.href).searchParams.get("variant");
      if (!id) return null;
      return product.variants.find((v) => String(v.id) === id) || null;
    }

    function money(cents) {
      return formatMoney(cents, moneyFormat)
        .replace(/<[^>]+>/g, "")
        .replace(/(\d)\.00\b/g, "$1");
    }

    function updatePrice(variant) {
      const wrap = document.querySelector(".product-info__price");
      if (!wrap) return;
      const currentEl = wrap.querySelector("[data-price-current]");
      if (currentEl) currentEl.textContent = money(variant.price);

      const onSale =
        typeof variant.compare_at_price === "number" &&
        variant.compare_at_price > variant.price;
      let compareEl = wrap.querySelector("[data-price-compare]");
      if (onSale) {
        if (!compareEl) {
          compareEl = document.createElement("span");
          compareEl.className = "price__compare";
          compareEl.setAttribute("data-price-compare", "");
          wrap.insertBefore(compareEl, currentEl || null);
        }
        compareEl.textContent = money(variant.compare_at_price);
        wrap.classList.add("price--on-sale");
      } else {
        if (compareEl) compareEl.remove();
        wrap.classList.remove("price--on-sale");
      }
    }

    function syncVariantUrl(variant) {
      if (!variant || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    }

    function updateStockStatus(variant) {
      if (!stockStatusEl) return;

      const qty =
        typeof variant.inventory_quantity === "number" ? variant.inventory_quantity : 0;

      if (variant.inventory_management !== "shopify" || !variant.available || qty < 1) {
        stockStatusEl.hidden = true;
        stockStatusEl.textContent = "";
        return;
      }

      stockStatusEl.hidden = false;
      stockStatusEl.textContent = inStockText;
    }

    function syncOptionButtons(variant) {
      if (!variant?.options) return;
      document.querySelectorAll(".variant-selector__group").forEach((group, i) => {
        const value = variant.options[i];
        group.querySelectorAll(".variant-selector__option").forEach((btn) => {
          const active = btn.dataset.optionValue === value;
          btn.classList.toggle("is-active", active);
          btn.setAttribute("aria-checked", active ? "true" : "false");
        });
        const label = group.querySelector("[data-option-selected], [data-colour-selected]");
        if (label) label.textContent = value || "";
      });
    }

    function updateOptionAvailability(selected) {
      document.querySelectorAll(".variant-selector__group").forEach((group, groupIndex) => {
        group.querySelectorAll(".variant-selector__option").forEach((btn) => {
          const value = btn.dataset.optionValue;
          const available = product.variants.some((variant) => {
            if (variant.options[groupIndex] !== value) return false;
            return variant.options.every((opt, i) => {
              if (i === groupIndex) return true;
              if (!selected[i]) return true;
              return opt === selected[i];
            }) && variant.available;
          });
          btn.classList.toggle("is-unavailable", !available);
          btn.title = available ? value : `${value} (${labelSoldOut})`;
          if (available) {
            btn.removeAttribute("aria-disabled");
          } else {
            btn.setAttribute("aria-disabled", "true");
          }
        });
      });
    }

    function syncStickyThumb(variant) {
      if (!stickyThumb || !variant?.featured_media_id) return;
      const media = productMedia.find((m) => m.id === variant.featured_media_id);
      if (!media?.thumb) return;
      stickyThumb.src = media.thumb;
      stickyThumb.removeAttribute("srcset");
    }

    function updateVariantUI(variant, options) {
      if (!variant) return;
      variantInput.value = variant.id;
      const available = !!variant.available;
      const label = available ? labelAdd : labelSoldOut;
      if (addBtn) {
        addBtn.disabled = !available;
        const addLabel = addBtn.querySelector("[data-add-to-cart-label]");
        const addPrice = addBtn.querySelector("[data-add-to-cart-price]");
        if (addLabel) {
          addLabel.textContent = label;
        } else {
          addBtn.textContent = label;
        }
        if (addPrice) {
          addPrice.hidden = !available;
          if (available) addPrice.textContent = money(variant.price);
        }
      }
      if (stickyAddBtn) {
        stickyAddBtn.disabled = !available;
        stickyAddBtn.textContent = label;
      }
      if (stickySelect && stickySelect.value !== String(variant.id)) {
        stickySelect.value = String(variant.id);
      }
      if (stickyPriceEl) stickyPriceEl.textContent = money(variant.price);
      updatePrice(variant);
      updateStockStatus(variant);
      syncStickyThumb(variant);

      const colour =
        colourOptionIndex != null ? variant.options[colourOptionIndex] : "";
      if (colour) filterGalleryByColour(colour);
      if (variant.featured_media_id) {
        selectGalleryMedia(variant.featured_media_id, productMedia);
      }

      const selected = variant.options.slice();
      updateOptionAvailability(selected);
      syncOptionButtons(variant);
      if (options?.updateUrl) syncVariantUrl(variant);
    }

    optionButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const group = btn.closest(".variant-selector__group");
        group.querySelectorAll(".variant-selector__option").forEach((b) => {
          b.classList.remove("is-active");
          b.setAttribute("aria-checked", "false");
        });
        btn.classList.add("is-active");
        btn.setAttribute("aria-checked", "true");
        if (group.hasAttribute("data-colour-option")) {
          const label = group.querySelector("[data-colour-selected]");
          if (label) label.textContent = btn.dataset.optionValue || "";
        }
        const variant = findVariant();
        if (variant) updateVariantUI(variant, { updateUrl: true });
      });
    });

    stickySelect?.addEventListener("change", () => {
      const variant = product.variants.find((v) => String(v.id) === stickySelect.value);
      if (!variant) return;
      syncOptionButtons(variant);
      updateVariantUI(variant, { updateUrl: true });
    });

    const urlVariant = findVariantFromUrl();
    const initialVariant = urlVariant || findVariant();
    if (initialVariant) {
      syncOptionButtons(initialVariant);
      updateVariantUI(initialVariant, { updateUrl: !!urlVariant });
    }
  }

  /* Sticky ATC bar — show after main form leaves the viewport */
  if (productForm && stickyBar) {
    const stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        stickyBar.classList.toggle("is-visible", show);
        stickyBar.setAttribute("aria-hidden", show ? "false" : "true");
      },
      { threshold: 0 },
    );
    stickyObserver.observe(productForm);

    if (cartDrawer) {
      const syncStickyCartState = () => {
        stickyBar.classList.toggle("is-cart-open", cartDrawer.classList.contains("is-open"));
      };
      syncStickyCartState();
      new MutationObserver(syncStickyCartState).observe(cartDrawer, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    stickyAddBtn?.addEventListener("click", () => {
      if (typeof productForm.requestSubmit === "function") {
        productForm.requestSubmit();
      } else {
        productForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      }
    });
  }

  /* AJAX add to cart — one request with bundled drawer HTML */
  if (productForm) {
    productForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (productForm.dataset.adding === "true") return;

      const errorEl = document.querySelector("[data-cart-error]");
      if (errorEl) errorEl.hidden = true;

      const formData = new FormData(productForm);
      const qty = Math.max(1, parseInt(formData.get("quantity") || "1", 10) || 1);
      const current = parseInt(cartCount?.textContent || "0", 10) || 0;
      const redirectToCart = cartPageOnly.matches;

      if (!redirectToCart) {
        formData.append("sections", CART_DRAWER_SECTION_ID);
        formData.append("sections_url", "/cart");
      }

      productForm.dataset.adding = "true";
      if (addBtn) addBtn.disabled = true;
      if (stickyAddBtn) stickyAddBtn.disabled = true;
      if (!redirectToCart) {
        updateCartCount(current + qty);
        openCart();
        setCartDrawerUpdating(true);
      }

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          body: formData,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          updateCartCount(current);
          showCartError(err.description || "Could not add to cart");
          return;
        }
        const data = await res.json();
        if (redirectToCart) {
          goToCartPage();
          return;
        }
        if (data.sections?.[CART_DRAWER_SECTION_ID]) {
          applyCartResponse(data);
        } else {
          await refreshCartDrawer();
        }
      } catch (err) {
        updateCartCount(current);
        showCartError(err.message || "Could not add to cart");
        console.error(err);
      } finally {
        setCartDrawerUpdating(false);
        if (!isNavigatingToCart) {
          productForm.dataset.adding = "false";
          if (addBtn) addBtn.disabled = false;
          if (stickyAddBtn) stickyAddBtn.disabled = false;
        }
      }
    });
  }

  /* Collection sort */
  document.querySelectorAll("[data-collection-sort]").forEach((select) => {
    select.addEventListener("change", () => {
      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", select.value);
      window.location.href = url.toString();
    });
  });

  /* Unfiltered collections <= 250 render every product on one page. Old ?page= links go back there. */
  const showAllCollection = document.querySelector("[data-collection-show-all]");
  if (showAllCollection) {
    const showAllUrl = new URL(window.location.href);
    if (showAllUrl.searchParams.has("page")) {
      showAllUrl.searchParams.delete("page");
      window.location.replace(showAllUrl.toString());
    }
  }

  /* Collections larger than 250 cannot be reordered in one Liquid page. Fetch card batches and put in-stock first. */
  const availabilityGrid = document.querySelector("[data-availability-order]");
  if (availabilityGrid) {
    const total = Number(availabilityGrid.dataset.productsCount || 0);
    const pageSize = Number(availabilityGrid.dataset.pageSize || 24);
    const currentPage = Number(availabilityGrid.dataset.currentPage || 1);
    const oosHeading = availabilityGrid.dataset.oosHeading || "Out of stock";
    const batchCount = Math.max(1, Math.ceil(total / 250));

    (async () => {
      try {
        const cards = [];
        for (let page = 1; page <= batchCount; page += 1) {
          const url = new URL(window.location.href);
          url.searchParams.set(
            "view",
            availabilityGrid.dataset.officialNames === "true" ? "product-cards-official" : "product-cards",
          );
          url.searchParams.set("page", String(page));
          const res = await fetch(url.toString(), { headers: { Accept: "text/html" } });
          if (!res.ok) return;
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, "text/html");
          doc.querySelectorAll(".product-card").forEach((card) => cards.push(card));
        }
        if (!cards.length) return;

        cards.sort((a, b) => {
          const aIn = a.dataset.productAvailable === "true" ? 0 : 1;
          const bIn = b.dataset.productAvailable === "true" ? 0 : 1;
          return aIn - bIn;
        });

        const start = Math.max(0, (currentPage - 1) * pageSize);
        const pageCards = cards.slice(start, start + pageSize);
        const next = [];
        let headingInserted = false;
        pageCards.forEach((card) => {
          if (!headingInserted && card.dataset.productAvailable === "false") {
            const heading = document.createElement("h2");
            heading.className = "product-grid__status-heading";
            heading.textContent = oosHeading;
            next.push(heading);
            headingInserted = true;
          }
          next.push(card);
        });
        availabilityGrid.replaceChildren(...next);
      } catch (err) {
        console.error(err);
      }
    })();
  }

  /* Keep scroll position across filter full-page reloads */
  const FILTER_SCROLL_KEY = "heathers:filterScroll";

  function saveFilterScroll() {
    try {
      sessionStorage.setItem(
        FILTER_SCROLL_KEY,
        JSON.stringify({ path: window.location.pathname, y: window.scrollY }),
      );
    } catch (err) {
      /* sessionStorage unavailable */
    }
  }

  document.addEventListener("click", (e) => {
    const link = e.target.closest("a");
    if (!link || !link.href) return;
    if (!link.closest("[data-collection-filters], .collection-active-filters")) return;
    saveFilterScroll();
  });

  try {
    const savedScroll = JSON.parse(sessionStorage.getItem(FILTER_SCROLL_KEY) || "null");
    sessionStorage.removeItem(FILTER_SCROLL_KEY);
    if (savedScroll && savedScroll.path === window.location.pathname) {
      window.scrollTo(0, savedScroll.y);
      window.addEventListener("load", () => window.scrollTo(0, savedScroll.y), { once: true });
    }
  } catch (err) {
    /* sessionStorage unavailable */
  }

  /* Price range slider */
  document.querySelectorAll("[data-price-slider]").forEach((form) => {
    const rangeMin = form.querySelector("[data-price-range-min]");
    const rangeMax = form.querySelector("[data-price-range-max]");
    const inputMin = form.querySelector("[data-price-input-min]");
    const inputMax = form.querySelector("[data-price-input-max]");
    const fill = form.querySelector("[data-price-fill]");
    if (!rangeMin || !rangeMax || !inputMin || !inputMax) return;

    const ceiling = parseFloat(form.dataset.priceCeiling) || 0;
    const clamp = (v) => Math.min(Math.max(Number.isFinite(v) ? v : 0, 0), ceiling);

    function paint(lo, hi) {
      if (!fill || !ceiling) return;
      fill.style.left = `${(lo / ceiling) * 100}%`;
      fill.style.right = `${100 - (hi / ceiling) * 100}%`;
    }

    function sync(source) {
      let lo = clamp(parseFloat(source === "input" ? inputMin.value : rangeMin.value));
      let hi = clamp(parseFloat(source === "input" ? inputMax.value : rangeMax.value));
      if (lo > hi) {
        [lo, hi] = [hi, lo];
      }
      rangeMin.value = String(lo);
      rangeMax.value = String(hi);
      inputMin.value = String(lo);
      inputMax.value = String(hi);
      paint(lo, hi);
    }

    rangeMin.addEventListener("input", () => {
      if (parseFloat(rangeMin.value) > parseFloat(rangeMax.value)) {
        rangeMin.value = rangeMax.value;
      }
      sync("range");
    });
    rangeMax.addEventListener("input", () => {
      if (parseFloat(rangeMax.value) < parseFloat(rangeMin.value)) {
        rangeMax.value = rangeMin.value;
      }
      sync("range");
    });
    inputMin.addEventListener("input", () => sync("input"));
    inputMax.addEventListener("input", () => sync("input"));

    [rangeMin, rangeMax].forEach((el) => {
      el.addEventListener("change", () => {
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit();
        } else {
          form.submit();
        }
      });
    });

    form.addEventListener("submit", () => {
      saveFilterScroll();
      if ((parseFloat(inputMin.value) || 0) <= 0) inputMin.disabled = true;
      if ((parseFloat(inputMax.value) || ceiling) >= ceiling) inputMax.disabled = true;
    });

    sync("range");
  });

  /* Collection filters drawer (mobile) — shares Escape / focus trap / scroll lock */
  const collectionFilters = document.querySelector("[data-collection-filters]");
  const collectionFiltersBackdrop = document.querySelector(".collection-filters__backdrop");
  const collectionFiltersOpenBtn = document.querySelector("[data-collection-filters-open]");

  function syncFiltersBackdrop(open) {
    if (!collectionFiltersBackdrop) return;
    if (open) {
      collectionFiltersBackdrop.removeAttribute("hidden");
      collectionFiltersBackdrop.classList.add("is-visible");
    } else {
      collectionFiltersBackdrop.setAttribute("hidden", "");
      collectionFiltersBackdrop.classList.remove("is-visible");
    }
  }

  function openCollectionFilters() {
    if (!collectionFilters) return;
    collectionFilters.setAttribute("role", "dialog");
    collectionFilters.setAttribute("aria-modal", "true");
    openOverlay({
      root: collectionFilters,
      panel: collectionFilters,
      initialFocus: collectionFilters.querySelector("[data-collection-filters-close]"),
      onClose: () => {
        syncFiltersBackdrop(false);
        collectionFilters.removeAttribute("role");
        collectionFilters.removeAttribute("aria-modal");
        /* Sticky desktop filters must stay exposed to assistive tech */
        collectionFilters.removeAttribute("aria-hidden");
        collectionFiltersOpenBtn?.setAttribute("aria-expanded", "false");
      },
    });
    syncFiltersBackdrop(true);
    collectionFiltersOpenBtn?.setAttribute("aria-expanded", "true");
  }

  function closeCollectionFilters() {
    if (activeOverlay?.root === collectionFilters) closeOverlay();
  }

  document.querySelectorAll("[data-collection-filters-open]").forEach((button) => {
    button.addEventListener("click", openCollectionFilters);
  });

  document.querySelectorAll("[data-collection-filters-close]").forEach((button) => {
    button.addEventListener("click", closeCollectionFilters);
  });

  /* Collection SEO: turn the FAQ heading + h3 questions into collapsed details */
  function escapeSeoText(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function renderShopifyRichText(node) {
    if (!node) return "";
    if (Array.isArray(node)) return node.map(renderShopifyRichText).join("");
    if (node.type === "text") {
      let value = escapeSeoText(node.value || "");
      if (node.bold) value = `<strong>${value}</strong>`;
      if (node.italic) value = `<em>${value}</em>`;
      return value;
    }
    const inner = renderShopifyRichText(node.children || []);
    switch (node.type) {
      case "root":
        return inner;
      case "heading": {
        const level = node.level || 2;
        return `<h${level}>${inner}</h${level}>`;
      }
      case "paragraph":
        return `<p>${inner}</p>`;
      case "list": {
        const tag = node.listType === "ordered" ? "ol" : "ul";
        return `<${tag}>${inner}</${tag}>`;
      }
      case "list-item":
        return `<li>${inner}</li>`;
      case "link": {
        const href = node.url ? ` href="${escapeSeoText(node.url)}"` : "";
        return `<a${href}>${inner}</a>`;
      }
      default:
        return inner;
    }
  }

  function hydrateCollectionSeoJson(root) {
    const raw = (root.textContent || "").trim();
    if (!raw.startsWith('{"type":"root"')) return;
    try {
      root.innerHTML = renderShopifyRichText(JSON.parse(raw));
    } catch {
      /* leave as-is */
    }
  }

  function accordionizeCollectionFaq(root) {
    if (!root) return;
    const scope = root.querySelector(".metafield-rich_text_field") || root;
    const heading = Array.from(scope.querySelectorAll("h2")).find((el) =>
      /faq/i.test(el.textContent || ""),
    );
    if (!heading) return;

    let node = heading.nextElementSibling;
    while (node && node.tagName !== "H3" && node.tagName !== "H2") {
      node = node.nextElementSibling;
    }

    while (node && node.tagName !== "H2") {
      if (node.tagName !== "H3") {
        node = node.nextElementSibling;
        continue;
      }

      const details = document.createElement("details");
      details.className = "collection-faq__item";
      const summary = document.createElement("summary");
      summary.textContent = (node.textContent || "").trim();
      details.appendChild(summary);

      const answer = document.createElement("div");
      answer.className = "collection-faq__answer";
      let cursor = node.nextElementSibling;
      while (cursor && cursor.tagName !== "H3" && cursor.tagName !== "H2") {
        const next = cursor.nextElementSibling;
        answer.appendChild(cursor);
        cursor = next;
      }
      details.appendChild(answer);
      node.replaceWith(details);
      node = details.nextElementSibling;
    }
  }

  document.querySelectorAll("[data-collection-seo-content]").forEach((root) => {
    hydrateCollectionSeoJson(root);
    accordionizeCollectionFaq(root);
  });

  /* Collection hero "Read more": expand the collapsed description in place */
  const collectionCopy = document.querySelector("[data-collection-copy]");
  const collectionReadMore = document.querySelector("[data-collection-read-more]");

  if (collectionCopy && collectionReadMore) {
    const extraBlocks = Array.from(collectionCopy.children).filter((el) => {
      if (el.hasAttribute("data-collection-read-more")) return false;
      return (el.textContent || "").replace(/\u00a0/g, " ").trim().length > 0;
    });

    if (extraBlocks.length > 1) {
      const expandLabel = collectionReadMore.dataset.labelExpand || collectionReadMore.textContent;
      const collapseLabel = collectionReadMore.dataset.labelCollapse || "";

      function setCollectionCopyExpanded(expanded) {
        collectionCopy.classList.toggle("is-expanded", expanded);
        collectionCopy.classList.toggle("is-collapsed", !expanded);
        collectionReadMore.setAttribute("aria-expanded", expanded ? "true" : "false");
        collectionReadMore.textContent = expanded ? collapseLabel : expandLabel;
      }

      collectionReadMore.hidden = false;
      setCollectionCopyExpanded(false);
      collectionReadMore.addEventListener("click", () => {
        const open = collectionReadMore.getAttribute("aria-expanded") === "true";
        setCollectionCopyExpanded(!open);
      });
    } else {
      collectionCopy.classList.remove("is-collapsed");
      collectionReadMore.hidden = true;
    }
  }

  /* Predictive search */
  const searchWrap = document.querySelector("[data-predictive-search-wrap]");
  const searchInput = document.querySelector("[data-predictive-search-input]");
  const searchResults = document.querySelector("[data-predictive-search-results]");
  let searchTimer;
  let searchSeq = 0;
  let suggestIndexPromise = null;
  let suggestIndexProducts = null;
  const SUGGEST_INDEX_CACHE_KEY = "hh-suggest-index";
  const SUGGEST_INDEX_CACHE_MS = 30 * 60 * 1000;
  const SUGGEST_INDEX_PAGE_SIZE = 250;

  function tokenizeSearch(value) {
    return String(value || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  function productPath(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url;
    }
  }

  function parseSuggestIndex(text) {
    const start = text.indexOf("{");
    const end = text.lastIndexOf("}");
    if (start === -1 || end === -1) return null;
    return JSON.parse(text.slice(start, end + 1));
  }

  function suggestIndexPageUrl(base, page) {
    const url = new URL(base, window.location.origin);
    if (page > 1) url.searchParams.set("page", String(page));
    return `${url.pathname}${url.search}`;
  }

  async function fetchSuggestIndexPage(base, page) {
    const res = await fetch(suggestIndexPageUrl(base, page));
    if (!res.ok) return null;
    return parseSuggestIndex(await res.text());
  }

  function suggestIndexBase() {
    return (
      searchWrap?.getAttribute("data-suggest-index") ||
      document.querySelector("[data-official-search]")?.getAttribute("data-suggest-index") ||
      ""
    );
  }

  function suggestIndexCount() {
    return Number(
      searchWrap?.getAttribute("data-suggest-index-count") ||
        document.querySelector("[data-official-search]")?.getAttribute("data-suggest-index-count") ||
        0,
      10,
    );
  }

  function readSuggestIndexCache(count, allowStale) {
    try {
      const data = JSON.parse(localStorage.getItem(SUGGEST_INDEX_CACHE_KEY) || "");
      if (!data || !Array.isArray(data.products)) return null;
      if (count && data.count !== count) return null;
      const stale = Date.now() - Number(data.at || 0) > SUGGEST_INDEX_CACHE_MS;
      if (stale && !allowStale) return null;
      return { products: data.products, stale };
    } catch {
      return null;
    }
  }

  function writeSuggestIndexCache(count, products) {
    try {
      localStorage.setItem(
        SUGGEST_INDEX_CACHE_KEY,
        JSON.stringify({ count, at: Date.now(), products }),
      );
    } catch {
      /* quota or private mode */
    }
  }

  async function fetchAllSuggestIndexPages(base, count) {
    let pages = count > 0 ? Math.max(1, Math.ceil(count / SUGGEST_INDEX_PAGE_SIZE)) : 1;
    const fetched = [];
    const requestPage = (page) =>
      fetchSuggestIndexPage(base, page).then((data) => {
        fetched[page - 1] = data;
        return data;
      });

    const firstBatch = [];
    for (let page = 1; page <= pages; page += 1) firstBatch.push(requestPage(page));
    await Promise.all(firstBatch);

    const reported = fetched[0]?.pages || pages;
    if (reported > pages) {
      const extra = [];
      for (let page = pages + 1; page <= reported; page += 1) extra.push(requestPage(page));
      await Promise.all(extra);
    }

    return fetched.flatMap((data) => data?.products || []);
  }

  function loadSuggestIndex() {
    if (suggestIndexPromise) return suggestIndexPromise;
    const base = suggestIndexBase();
    const count = suggestIndexCount();
    if (!base) {
      suggestIndexProducts = [];
      suggestIndexPromise = Promise.resolve([]);
      return suggestIndexPromise;
    }

    const fresh = readSuggestIndexCache(count, false);
    if (fresh) {
      suggestIndexProducts = fresh.products;
      suggestIndexPromise = Promise.resolve(fresh.products);
      return suggestIndexPromise;
    }

    const stale = readSuggestIndexCache(count, true);
    if (stale) suggestIndexProducts = stale.products;

    suggestIndexPromise = (async () => {
      try {
        const products = await fetchAllSuggestIndexPages(base, count);
        suggestIndexProducts = products;
        writeSuggestIndexCache(count, products);
        return products;
      } catch {
        return suggestIndexProducts || [];
      }
    })();
    return suggestIndexPromise;
  }

  function warmSuggestIndex() {
    if (suggestIndexProducts) return suggestIndexProducts;
    return readSuggestIndexCache(suggestIndexCount(), true)?.products || [];
  }

  function prefetchSuggestIndex() {
    const start = () => loadSuggestIndex();
    if ("requestIdleCallback" in window) {
      requestIdleCallback(start, { timeout: 2500 });
    } else {
      window.setTimeout(start, 800);
    }
  }

  function matchesOfficialIndex(product, query) {
    const qTokens = tokenizeSearch(query);
    if (!qTokens.length) return false;
    const hayTokens = tokenizeSearch([product.t, product.o].filter(Boolean).join(" "));
    return qTokens.every((qt) => hayTokens.some((ht) => ht.startsWith(qt)));
  }

  function scoreOfficialIndex(product, query) {
    const qTokens = tokenizeSearch(query);
    const official = tokenizeSearch(product.o);
    const title = tokenizeSearch(product.t);
    let score = 0;
    qTokens.forEach((qt) => {
      if (official.some((token) => token.startsWith(qt))) score += 3;
      if (title.some((token) => token.startsWith(qt))) score += 2;
      if (official[0] && official[0].startsWith(qt)) score += 2;
      if (title[0] && title[0].startsWith(qt)) score += 1;
    });
    return score;
  }

  async function fetchShopifySuggest(query) {
    const root = window.Shopify?.routes?.root || "/";
    const res = await fetch(
      `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last`,
    );
    if (!res.ok) return [];
    const data = await res.json();
    return data.resources?.results?.products || [];
  }

  function mergeSuggestResults(shopifyProducts, index, query) {
    const byPath = new Map();
    index.forEach((product) => {
      if (product?.u) byPath.set(productPath(product.u), product);
    });

    const seen = new Set();
    const merged = [];

    shopifyProducts.forEach((product) => {
      const key = productPath(product.url);
      if (!key || seen.has(key)) return;
      seen.add(key);
      const local = byPath.get(key);
      merged.push({
        url: product.url,
        title: product.title,
        official: local?.o || "",
        image: product.featured_image?.url || local?.i || "",
      });
    });

    index
      .filter((product) => matchesOfficialIndex(product, query))
      .sort((a, b) => scoreOfficialIndex(b, query) - scoreOfficialIndex(a, query))
      .forEach((product) => {
        const key = productPath(product.u);
        if (!key || seen.has(key)) return;
        seen.add(key);
        merged.push({
          url: product.u,
          title: product.t,
          official: product.o || "",
          image: product.i || "",
        });
      });

    return merged.slice(0, 5);
  }

  function renderSuggestResults(products) {
    searchResults.replaceChildren();
    products.forEach((product) => {
      const link = document.createElement("a");
      link.href = product.url;
      link.className = "predictive-search__item";
      if (product.image) {
        const img = document.createElement("img");
        img.src = product.image;
        img.alt = "";
        img.width = 40;
        img.height = 40;
        link.appendChild(img);
      }
      const text = document.createElement("span");
      text.className = "predictive-search__item-text";
      const title = document.createElement("span");
      title.className = "predictive-search__item-title";
      title.textContent = product.title;
      text.appendChild(title);
      const official = String(product.official || "").trim();
      if (official && official.toLowerCase() !== String(product.title || "").toLowerCase()) {
        const subtitle = document.createElement("span");
        subtitle.className = "predictive-search__item-official";
        subtitle.textContent = official;
        text.appendChild(subtitle);
      }
      link.appendChild(text);
      searchResults.appendChild(link);
    });
  }

  if (searchWrap && searchInput && searchResults) {
    /* Sticky headers scroll back to their in-flow position when an input
       inside them is focused. Capture scrollY before focus, then pin it. */
    let searchPinnedY = null;
    let searchScrollLock = null;

    function pinSearchScroll(y) {
      searchPinnedY = y;
      if (searchScrollLock) return;
      const restore = () => {
        if (searchPinnedY == null) return;
        if (Math.abs(window.scrollY - searchPinnedY) > 1) {
          window.scrollTo(0, searchPinnedY);
        }
      };
      searchScrollLock = restore;
      window.addEventListener("scroll", restore);
      restore();
      requestAnimationFrame(() => {
        restore();
        requestAnimationFrame(() => {
          restore();
          window.setTimeout(() => {
            restore();
            window.removeEventListener("scroll", restore);
            searchScrollLock = null;
            searchPinnedY = null;
          }, 150);
        });
      });
    }

    searchWrap.addEventListener(
      "pointerdown",
      () => {
        pinSearchScroll(window.scrollY);
      },
      true,
    );

    searchInput.addEventListener("mousedown", (e) => {
      if (e.button !== 0 || document.activeElement === searchInput) return;
      e.preventDefault();
      focusEl(searchInput);
    });

    document.addEventListener(
      "keydown",
      (e) => {
        if (e.key !== "Tab") return;
        searchPinnedY = window.scrollY;
      },
      true,
    );

    function paintSuggest(products) {
      const scrollY = window.scrollY;
      if (!products.length) {
        searchResults.classList.remove("is-visible");
        searchResults.replaceChildren();
        return;
      }
      renderSuggestResults(products);
      searchResults.classList.add("is-visible");
      if (Math.abs(window.scrollY - scrollY) > 1) window.scrollTo(0, scrollY);
    }

    searchWrap.addEventListener("focusin", () => {
      pinSearchScroll(searchPinnedY != null ? searchPinnedY : window.scrollY);
      loadSuggestIndex();
    });

    searchInput.addEventListener("input", () => {
      clearTimeout(searchTimer);
      loadSuggestIndex();
      const q = searchInput.value.trim();
      if (q.length < 2) {
        searchSeq += 1;
        searchResults.classList.remove("is-visible");
        searchResults.replaceChildren();
        return;
      }
      const delay = warmSuggestIndex().length ? 160 : 220;
      searchTimer = setTimeout(async () => {
        const seq = ++searchSeq;
        const indexPromise = loadSuggestIndex();
        const shopifyProducts = await fetchShopifySuggest(q);
        if (seq !== searchSeq) return;

        const first = mergeSuggestResults(shopifyProducts, warmSuggestIndex(), q);
        if (first.length) paintSuggest(first);

        const index = await indexPromise;
        if (seq !== searchSeq) return;
        const next = mergeSuggestResults(shopifyProducts, index, q);
        if (next.length) paintSuggest(next);
        else if (!first.length) paintSuggest([]);
      }, delay);
    });

    prefetchSuggestIndex();

    document.addEventListener("click", (e) => {
      if (!e.target.closest("[data-predictive-search-wrap]")) {
        searchResults.classList.remove("is-visible");
      }
    });
  }

  /* /search page: add official-name matches Shopify's index misses */
  (function initOfficialSearchPage() {
    const page = document.querySelector("[data-official-search]");
    if (!page) return;
    const terms = (page.dataset.officialSearchTerms || "").trim();
    if (terms.length < 2) return;

    const moneyFormat = page.dataset.moneyFormat || "";
    const soldOutLabel = page.dataset.soldOut || "Sold out";
    const extraLimit = 48;

    function officialSearchCard(product) {
      const available = product.a !== false;
      const link = document.createElement("a");
      link.href = product.u;
      link.className = available ? "product-card" : "product-card product-card--sold-out";
      link.dataset.productPrice = String(product.p || 0);
      link.dataset.productAvailable = available ? "true" : "false";

      const media = document.createElement("div");
      media.className = "product-card__media";
      if (!available) {
        const badge = document.createElement("span");
        badge.className = "product-card__badge product-card__badge--sold-out";
        badge.textContent = soldOutLabel;
        media.appendChild(badge);
      }
      if (product.i) {
        const img = document.createElement("img");
        img.className = "product-card__image";
        img.src = product.i;
        img.alt = product.t || "";
        img.loading = "lazy";
        img.width = 400;
        img.height = 400;
        media.appendChild(img);
      }
      link.appendChild(media);

      const info = document.createElement("div");
      info.className = "product-card__info";
      const title = document.createElement("h3");
      title.className = "product-card__title";
      title.textContent = product.t;
      info.appendChild(title);
      const official = String(product.o || "").trim();
      if (official && official.toLowerCase() !== String(product.t || "").toLowerCase()) {
        const subtitle = document.createElement("p");
        subtitle.className = "product-card__subtitle";
        subtitle.textContent = official;
        info.appendChild(subtitle);
      }
      const meta = document.createElement("div");
      meta.className = "product-card__meta";
      const price = document.createElement("p");
      price.className = "price product-card__price";
      const current = document.createElement("span");
      current.className = "price__current";
      current.textContent = formatMoney(product.p || 0, moneyFormat).replace(/<[^>]+>/g, "");
      price.appendChild(current);
      meta.appendChild(price);
      info.appendChild(meta);
      link.appendChild(info);
      return link;
    }

    function ensureSearchGrid() {
      let grid = page.querySelector(".product-grid");
      if (grid) return grid;
      const main = page.querySelector(".collection-main");
      if (!main) return null;
      grid = document.createElement("div");
      grid.className = "product-grid product-grid--3";
      const recovery = main.querySelector(".search-recovery");
      if (recovery) {
        recovery.before(grid);
      } else {
        main.appendChild(grid);
      }
      return grid;
    }

    function updateSearchCounts(count) {
      page.querySelectorAll(".collection-count").forEach((el) => {
        el.textContent = `${count} results`;
      });
      const termsEl = page.querySelector(".search-page__terms");
      if (termsEl) {
        termsEl.textContent = `Showing ${count} results for "${terms}"`;
      }
    }

    loadSuggestIndex().then((index) => {
      const existing = new Set(
        [...page.querySelectorAll(".product-card")].map((card) => productPath(card.href)),
      );
      const extra = index
        .filter((product) => matchesOfficialIndex(product, terms))
        .filter((product) => !existing.has(productPath(product.u)))
        .sort((a, b) => {
          const avail = (b.a !== false) - (a.a !== false);
          if (avail) return avail;
          return scoreOfficialIndex(b, terms) - scoreOfficialIndex(a, terms);
        })
        .slice(0, extraLimit);
      if (!extra.length) return;

      const grid = ensureSearchGrid();
      if (!grid) return;
      const recovery = page.querySelector(".search-recovery");
      if (recovery) recovery.hidden = true;

      const cards = extra.map(officialSearchCard);
      grid.prepend(...cards);
      updateSearchCounts(existing.size + extra.length);
    });
  })();

  /* Product dwell time -> dataLayer */
  (function trackProductDwell() {
    const productEl = document.querySelector("[data-product-json]");
    if (!productEl) return;

    let product;
    try {
      product = JSON.parse(productEl.textContent);
    } catch (e) {
      return;
    }

    let activeMs = 0;
    let lastResume = performance.now();
    let sent = false;

    function sendDwell() {
      if (sent) return;
      if (document.visibilityState !== "hidden") {
        activeMs += performance.now() - lastResume;
      }
      const seconds = Math.round(activeMs / 1000);
      if (seconds < 2) return;
      sent = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "product_dwell",
        product_id: product.id,
        product_title: product.title,
        product_vendor: product.vendor,
        seconds: seconds,
      });
    }

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        activeMs += performance.now() - lastResume;
        sendDwell();
      } else {
        lastResume = performance.now();
      }
    });
    window.addEventListener("pagehide", sendDwell);
  })();

  /* Judge.me star badge gating — hide the summary badge until a product has at
     least `data-hh-reviews-min` reviews, unless preview mode is on. The full
     review widget is never gated. Judge.me renders badges client-side and sets
     `data-number-of-reviews` on `.jdgm-prev-badge`, so we watch for that. */
  (function initReviewBadgeGating() {
    const wraps = document.querySelectorAll("[data-hh-reviews]");
    if (!wraps.length) return;

    function isSummary(wrap) {
      return wrap.classList.contains("hh-reviews-badge--summary");
    }

    function starRow(rating) {
      let html = "";
      for (let i = 1; i <= 5; i += 1) {
        const filled = rating >= i - 0.25;
        html += `<span class="hh-star hh-star--${filled ? "full" : "empty"}" aria-hidden="true">★</span>`;
      }
      return html;
    }

    function readStats(wrap) {
      const badge = wrap.querySelector(".jdgm-prev-badge");
      if (!badge) return { count: 0, rating: 0 };
      const count = parseInt(badge.getAttribute("data-number-of-reviews") || "0", 10) || 0;
      const rating = parseFloat(badge.getAttribute("data-average-rating") || "0") || 0;
      return { count, rating };
    }

    // Draw theme-owned stars + count into our own display node. Writes are
    // equality-guarded so re-running (e.g. from the observer catching Judge.me
    // populating the badge) settles after one pass and never loops.
    function render(wrap) {
      const min = parseInt(wrap.dataset.hhReviewsMin || "0", 10) || 0;
      const preview = wrap.dataset.hhReviewsPreview === "true";
      const { count, rating } = readStats(wrap);
      const ready = preview || count >= min;

      if (ready !== wrap.classList.contains("is-ready")) {
        wrap.classList.toggle("is-ready", ready);
        if (ready && isSummary(wrap)) {
          document.dispatchEvent(new CustomEvent("hh:reviews-summary-ready"));
        }
      }
      if (!ready || !count) return;

      const display = wrap.querySelector("[data-hh-reviews-display]");
      if (!display) return;
      const starsEl = display.querySelector(".hh-reviews-summary__stars");
      const countEl = display.querySelector(".hh-reviews-summary__count");
      if (!starsEl || !countEl) return;

      const starsHtml = starRow(rating);
      const ratingText = Number.isFinite(rating) ? rating.toFixed(1) : "";
      const countText =
        isSummary(wrap) && ratingText
          ? `${ratingText} · ${count} ${count === 1 ? "review" : "reviews"}`
          : `(${count})`;
      if (starsEl.innerHTML !== starsHtml) starsEl.innerHTML = starsHtml;
      if (countEl.textContent !== countText) countEl.textContent = countText;
      if (display.hidden) display.hidden = false;
    }

    wraps.forEach((wrap) => {
      render(wrap);

      // Watch only Judge.me's data attributes and child injection — never
      // `class`, which we toggle above.
      const observer = new MutationObserver(() => render(wrap));
      observer.observe(wrap, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["data-number-of-reviews", "data-average-rating"],
      });
    });
  })();

  /* Product page reviews — accordion when there are reviews; static empty
     state (with write-first CTA) when there are none. Inline summary jumps here. */
  (function initProductReviewsCollapse() {
    const section = document.querySelector("[data-product-reviews]");
    if (!section) return;

    const toggle = section.querySelector("[data-reviews-toggle]");
    const panel = section.querySelector("[data-reviews-panel]");
    const empty = section.querySelector("[data-reviews-empty]");
    const writeFirst = section.querySelector("[data-reviews-write-first]");
    const meta = section.querySelector("[data-reviews-toggle-meta]");
    const hint = section.querySelector("[data-reviews-toggle-hint]");
    const summaryBadge = document.querySelector(".hh-reviews-badge--summary[data-hh-reviews-jump]");
    if (!toggle || !panel) return;

    const expandLabel = section.dataset.reviewsExpand || "Read all reviews";
    const collapseLabel = section.dataset.reviewsCollapse || "Hide reviews";
    let mode = "pending"; // pending | empty | list

    function readBadgeStats() {
      const badge = document.querySelector(".hh-reviews-badge--summary .jdgm-prev-badge");
      if (!badge) return null;
      // Attribute must be present before we decide empty vs list — Judge.me
      // injects it after load. Missing attribute ≠ confirmed zero.
      if (!badge.hasAttribute("data-number-of-reviews")) return null;
      const count = parseInt(badge.getAttribute("data-number-of-reviews") || "0", 10) || 0;
      const rating = parseFloat(badge.getAttribute("data-average-rating") || "0") || 0;
      return { count, rating };
    }

    function setExpanded(expanded) {
      section.classList.toggle("is-expanded", expanded);
      toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      panel.setAttribute("aria-hidden", expanded ? "false" : "true");
      if (hint) hint.textContent = expanded ? collapseLabel : expandLabel;
    }

    function expand({ scroll = false } = {}) {
      if (mode === "empty") {
        // Writing the first review: reveal the Judge.me panel under the empty copy.
        section.classList.add("is-writing");
        setExpanded(true);
      } else {
        setExpanded(true);
      }
      if (scroll) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    function openWriteReview() {
      expand({ scroll: true });
      const tryClick = () => {
        const btn =
          panel.querySelector(".jdgm-write-rev-link") ||
          panel.querySelector(".jdgm-btn.jdgm-btn--solid") ||
          panel.querySelector("[data-from='reviews-widget']");
        if (btn) {
          btn.click();
          return true;
        }
        return false;
      };
      if (tryClick()) return;
      // Judge.me may still be mounting — retry briefly.
      let attempts = 0;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (tryClick() || attempts >= 10) window.clearInterval(timer);
      }, 200);
    }

    function applyMode(next) {
      if (next === mode) return;
      mode = next;
      section.classList.remove("product-reviews--pending", "product-reviews--empty");
      section.classList.toggle("product-reviews--empty", next === "empty");
      if (next === "pending") section.classList.add("product-reviews--pending");

      if (empty) empty.hidden = next !== "empty";
      toggle.hidden = next !== "list";

      if (next === "empty") {
        setExpanded(false);
        section.classList.remove("is-writing");
      }
    }

    // Stars/count in the summary are drawn by initReviewBadges; here we sync
    // accordion meta and switch empty vs list once Judge.me reports a count.
    // If Judge.me never injects a count (slow/blocked), fall back to empty so
    // we never leave a blank pending shell — better a write-first CTA than
    // empty stars or a stuck loading state.
    let settled = false;
    function syncFromBadge() {
      const stats = readBadgeStats();
      if (!stats) return;

      settled = true;
      if (stats.count === 0) {
        applyMode("empty");
        return;
      }

      applyMode("list");
      if (!meta) return;
      const metaText = `${stats.rating.toFixed(1)} · ${stats.count} review${stats.count === 1 ? "" : "s"}`;
      if (meta.textContent !== metaText) meta.textContent = metaText;
      meta.hidden = false;
    }

    window.setTimeout(() => {
      if (!settled && mode === "pending") applyMode("empty");
    }, 3500);

    toggle.addEventListener("click", () => {
      if (mode !== "list") return;
      setExpanded(!section.classList.contains("is-expanded"));
    });

    if (writeFirst) {
      writeFirst.addEventListener("click", openWriteReview);
    }

    if (summaryBadge) {
      function bindSummaryJump() {
        if (!summaryBadge.classList.contains("is-ready")) return;
        summaryBadge.setAttribute("role", "button");
        summaryBadge.setAttribute("tabindex", "0");
        summaryBadge.setAttribute("aria-label", expandLabel);
      }

      summaryBadge.addEventListener("click", () => {
        if (!summaryBadge.classList.contains("is-ready")) return;
        expand({ scroll: true });
      });
      summaryBadge.addEventListener("keydown", (event) => {
        if (!summaryBadge.classList.contains("is-ready")) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          expand({ scroll: true });
        }
      });

      bindSummaryJump();
      const summaryObserver = new MutationObserver(bindSummaryJump);
      summaryObserver.observe(summaryBadge, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    syncFromBadge();
    document.addEventListener("hh:reviews-summary-ready", syncFromBadge);
    const badgeWrap = document.querySelector(".hh-reviews-badge--summary");
    if (badgeWrap) {
      const badge = badgeWrap.querySelector(".jdgm-prev-badge");
      if (badge) {
        const statsObserver = new MutationObserver(syncFromBadge);
        statsObserver.observe(badge, {
          attributes: true,
          attributeFilter: ["data-number-of-reviews", "data-average-rating"],
        });
      }
      // Also watch for Judge.me injecting the badge node itself.
      const injectObserver = new MutationObserver(syncFromBadge);
      injectObserver.observe(badgeWrap, { childList: true, subtree: true });
      const readyObserver = new MutationObserver(() => {
        if (badgeWrap.classList.contains("is-ready")) syncFromBadge();
      });
      readyObserver.observe(badgeWrap, {
        attributes: true,
        attributeFilter: ["class"],
      });
      // If Judge.me never reports a count, keep the widget reachable.
      window.setTimeout(() => {
        if (mode === "pending") applyMode("list");
      }, 4000);
    } else {
      // No summary badge on the page — fall back to list mode so the widget
      // remains reachable.
      applyMode("list");
    }
  })();

  (function initAnnouncementBar() {
    const root = document.querySelector("[data-announcement-bar]");
    if (!root) return;

    const messages = Array.from(root.querySelectorAll("[data-announcement-message]"));
    if (messages.length <= 1) return;

    const prevBtn = root.querySelector("[data-announcement-prev]");
    const nextBtn = root.querySelector("[data-announcement-next]");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const rotateMs = Math.max(2, Number(root.dataset.rotateInterval) || 6) * 1000;
    const fadeMs = reduceMotion
      ? 0
      : Math.max(0, Number(root.dataset.fadeDuration) || 0.5) * 1000;
    let index = messages.findIndex((msg) => msg.classList.contains("is-active"));
    if (index < 0) index = 0;
    let timer = null;
    let paused = false;
    let animating = false;

    function wait(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    }

    function setActive(msg, active) {
      msg.classList.toggle("is-active", active);
      msg.setAttribute("aria-hidden", active ? "false" : "true");
      msg.querySelectorAll("a").forEach((link) => {
        if (active) link.removeAttribute("tabindex");
        else link.setAttribute("tabindex", "-1");
      });
    }

    async function show(nextIndex, { animate = true } = {}) {
      const target = ((nextIndex % messages.length) + messages.length) % messages.length;
      if (animating) return;
      if (target === index && messages[index].classList.contains("is-active")) return;

      animating = true;
      stop();

      const shouldAnimate = animate && fadeMs > 0;
      const current = messages[index];

      if (shouldAnimate && current.classList.contains("is-active")) {
        setActive(current, false);
        await wait(fadeMs);
      }

      messages.forEach((msg, i) => setActive(msg, i === target));
      index = target;

      if (shouldAnimate) await wait(fadeMs);

      animating = false;
      if (!paused) start();
    }

    function stop() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function start() {
      stop();
      if (paused || animating) return;
      timer = window.setTimeout(() => {
        show(index + 1);
      }, rotateMs);
    }

    prevBtn?.addEventListener("click", () => {
      show(index - 1);
    });
    nextBtn?.addEventListener("click", () => {
      show(index + 1);
    });

    root.addEventListener("mouseenter", () => {
      paused = true;
      stop();
    });
    root.addEventListener("mouseleave", () => {
      paused = false;
      start();
    });
    root.addEventListener("focusin", () => {
      paused = true;
      stop();
    });
    root.addEventListener("focusout", (e) => {
      if (root.contains(e.relatedTarget)) return;
      paused = false;
      start();
    });

    messages.forEach((msg, i) => setActive(msg, i === index));
    start();
  })();

  function initReveals() {
    const reveals = document.querySelectorAll(".reveal");
    if (!reveals.length) return;

    document.documentElement.classList.add("reveal-ready");

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      reveals.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    reveals.forEach((el) => {
      if (el.closest("[data-reveal='load']")) {
        el.classList.add("is-visible");
      }
    });

    const scrollReveals = [...reveals].filter((el) => !el.closest("[data-reveal='load']"));
    if (!scrollReveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.1 },
    );

    scrollReveals.forEach((el) => observer.observe(el));
  }

  initReveals();

  (function initLandingQuotes() {
    const root = document.querySelector("[data-landing-quotes]");
    if (!root) return;

    const track = root.querySelector("[data-landing-quotes-track]");
    const slides = track ? [...track.querySelectorAll(".landing-quotes__item")] : [];
    const dotsWrap = root.querySelector(".landing-quotes__dots");
    const dots = [...root.querySelectorAll("[data-landing-quotes-dot]")];
    if (!track || slides.length < 2) return;

    const mobile = window.matchMedia("(max-width: 47.99rem)");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const intervalMs = 5500;
    let index = 0;
    let timer = null;
    let held = false;

    const visibleCount = () => (mobile.matches ? 2 : slides.length);
    const pageCount = () => Math.max(1, slides.length - visibleCount() + 1);

    const cardStep = () => {
      const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
      return slides[0].getBoundingClientRect().width + gap;
    };

    const setDot = (i) => {
      const pages = pageCount();
      if (dotsWrap) dotsWrap.hidden = pages < 2;
      dots.forEach((dot, n) => {
        dot.hidden = n >= pages;
        if (n === i) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
    };

    const goTo = (i, smooth = true) => {
      const pages = pageCount();
      index = ((i % pages) + pages) % pages;
      const left = cardStep() * index;
      track.scrollTo({
        left,
        behavior: smooth && !reduceMotion.matches ? "smooth" : "auto",
      });
      setDot(index);
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (!mobile.matches || reduceMotion.matches || held || pageCount() < 2) return;
      timer = window.setInterval(() => goTo(index + 1), intervalMs);
    };

    dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const i = Number(dot.getAttribute("data-landing-quotes-dot"));
        if (Number.isFinite(i)) goTo(i);
        start();
      });
    });

    const hold = () => {
      held = true;
      stop();
    };
    const release = () => {
      held = false;
      start();
    };

    track.addEventListener("pointerdown", hold);
    track.addEventListener("pointerup", release);
    track.addEventListener("pointercancel", release);
    track.addEventListener("focusin", hold);
    track.addEventListener("focusout", release);

    let scrollTick;
    track.addEventListener(
      "scroll",
      () => {
        window.clearTimeout(scrollTick);
        scrollTick = window.setTimeout(() => {
          const step = cardStep();
          if (!step) return;
          index = Math.min(pageCount() - 1, Math.max(0, Math.round(track.scrollLeft / step)));
          setDot(index);
        }, 80);
      },
      { passive: true },
    );

    mobile.addEventListener("change", () => {
      if (mobile.matches) start();
      else {
        stop();
        track.scrollTo({ left: 0 });
        setDot(0);
      }
    });
    reduceMotion.addEventListener("change", start);

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) start();
        else stop();
      },
      { threshold: 0.35 },
    );
    io.observe(root);
  })();

  (function initLandingStickyCta() {
    const bar = document.querySelector("[data-landing-sticky-cta]");
    const heroCta = document.querySelector("[data-landing-hero-cta]");
    if (!bar || !heroCta) return;

    const syncState = () => {
      const cartOpen = Boolean(document.querySelector(".cart-drawer.is-open"));
      bar.classList.toggle("is-cart-open", cartOpen);
      bar.setAttribute("aria-hidden", bar.classList.contains("is-visible") && !cartOpen ? "false" : "true");
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        bar.classList.toggle("is-visible", !entry.isIntersecting);
        syncState();
      },
      { threshold: 0 },
    );
    io.observe(heroCta);

    const cartDrawerEl = document.querySelector("[data-cart-drawer]");
    if (cartDrawerEl) {
      const mo = new MutationObserver(syncState);
      mo.observe(cartDrawerEl, { attributes: true, attributeFilter: ["class", "aria-hidden"] });
    }
  })();

  (function initHeathersWordmarkReveal() {
    const roots = document.querySelectorAll("[data-heathers-wordmark-reveal]");
    if (!roots.length) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const supportsWAAPI = typeof Element !== "undefined" && typeof Element.prototype.animate === "function";

    function bbox(el) {
      const b = el.getBBox();
      return {
        x: b.x,
        y: b.y,
        w: b.width,
        h: b.height,
        cx: b.x + b.width / 2,
        cy: b.y + b.height / 2,
      };
    }

    function readNumber(el, key, fallback) {
      const raw = el.getAttribute(key);
      const n = Number(raw);
      return Number.isFinite(n) ? n : fallback;
    }

    function strokesOf(root) {
      return [...root.querySelectorAll(".heathers-wordmark-reveal__strokes path")];
    }

    function align(root) {
      const textLayer = root.querySelector(".heathers-wordmark-reveal__text-layer");
      const textEl = root.querySelector(".heathers-wordmark-reveal__text");
      const measure = root.querySelector(".heathers-wordmark-reveal__measure");
      if (!textLayer || !textEl || !measure) return false;

      textLayer.removeAttribute("transform");
      textEl.setAttribute("x", "40");
      textEl.setAttribute("y", "40");

      let tb;
      let pb;
      try {
        tb = bbox(textEl);
        pb = bbox(measure);
      } catch {
        return false;
      }

      if (!(tb.w > 0 && tb.h > 0 && pb.w > 0 && pb.h > 0)) return false;

      const dx = pb.x - tb.x;
      const dy = pb.cy - tb.cy;
      textLayer.setAttribute("transform", `translate(${dx.toFixed(2)} ${dy.toFixed(2)})`);
      return true;
    }

    function primeHidden(root) {
      const strokes = strokesOf(root);
      for (const path of strokes) {
        try {
          const len = path.getTotalLength();
          path.style.strokeDasharray = String(len);
          path.style.strokeDashoffset = String(len);
          if (path.getAnimations) {
            path.getAnimations().forEach((a) => a.cancel());
          }
        } catch {
          return false;
        }
      }
      return strokes.length > 0;
    }

    function cueHeroSubtitle() {
      document.dispatchEvent(new CustomEvent("heathers:hero-subtitle"));
    }

    function revealComplete(root) {
      strokesOf(root).forEach((path) => {
        try {
          const len = path.getTotalLength();
          path.style.strokeDasharray = String(len);
          path.style.strokeDashoffset = "0";
          if (path.getAnimations) {
            path.getAnimations().forEach((a) => a.cancel());
          }
        } catch {
          /* ignore */
        }
      });
      root.classList.add("is-ready", "is-complete");
      root.classList.remove("is-booting", "is-fallback");
      cueHeroSubtitle();
      document.dispatchEvent(
        new CustomEvent("heathers:wordmark-complete", { detail: { root } }),
      );
    }

    async function play(root) {
      const strokes = strokesOf(root);
      if (!strokes.length) return;

      const speed = readNumber(root, "data-speed", 2);
      const gap = readNumber(root, "data-gap", 0);
      const apoPause = readNumber(root, "data-apo-pause", 0.25);
      const base = readNumber(root, "data-base-speed", 420);

      if (!supportsWAAPI || reduceMotion) {
        revealComplete(root);
        return;
      }

      // Must already be primed hidden before is-ready, or title flashes then vanishes.
      if (!primeHidden(root)) {
        revealComplete(root);
        return;
      }

      let t = 0;
      const jobs = [];

      for (const path of strokes) {
        let len;
        try {
          len = path.getTotalLength();
        } catch {
          revealComplete(root);
          return;
        }
        const dur = Math.max(0.08, len / base / speed);
        if (path.getAttribute("data-apostrophe") === "true") t += apoPause;
        jobs.push({ path, len, dur, delay: t });
        t += dur + gap;
      }

      const lastLetter = [...jobs]
        .reverse()
        .find((job) => job.path.getAttribute("data-apostrophe") !== "true");
      if (lastLetter) {
        const cueMs = Math.max(0, (lastLetter.delay + lastLetter.dur) * 1000);
        setTimeout(cueHeroSubtitle, cueMs);
      }

      try {
        await Promise.all(
          jobs.map(({ path, len, dur, delay }) =>
            path.animate([{ strokeDashoffset: len }, { strokeDashoffset: 0 }], {
              duration: dur * 1000,
              delay: delay * 1000,
              fill: "forwards",
              easing: "linear",
            }).finished,
          ),
        );
        revealComplete(root);
      } catch {
        revealComplete(root);
      }
    }

    async function boot(root) {
      const textEl = root.querySelector(".heathers-wordmark-reveal__text");
      if (!textEl) return;

      try {
        if (document.fonts && document.fonts.load) {
          await Promise.race([
            Promise.all([document.fonts.load('120px "Alex Brush"'), document.fonts.ready]),
            new Promise((resolve) => setTimeout(resolve, 1800)),
          ]);
        }
      } catch {
        /* keep fallback */
      }

      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      if (!align(root)) {
        await new Promise((r) => setTimeout(r, 120));
        if (!align(root)) {
          root.classList.add("is-fallback");
          cueHeroSubtitle();
          document.dispatchEvent(
            new CustomEvent("heathers:wordmark-complete", { detail: { root } }),
          );
          return;
        }
      }

      if (reduceMotion || !supportsWAAPI) {
        revealComplete(root);
        return;
      }

      // Hide via mask BEFORE swapping fallback → SVG, so nothing flashes then disappears.
      if (!primeHidden(root)) {
        revealComplete(root);
        return;
      }

      root.classList.add("is-ready");
      root.classList.remove("is-booting", "is-fallback");

      const host = root.closest(".reveal");
      const start = () => {
        play(root);
      };

      if (!host || host.classList.contains("is-visible")) {
        start();
      } else {
        const obs = new MutationObserver(() => {
          if (host.classList.contains("is-visible")) {
            obs.disconnect();
            start();
          }
        });
        obs.observe(host, { attributes: true, attributeFilter: ["class"] });
        setTimeout(() => {
          obs.disconnect();
          if (!root.classList.contains("is-complete")) start();
        }, 2500);
      }

      // If animation never finishes, force the finished wordmark (never leave it blank).
      setTimeout(() => {
        if (!root.classList.contains("is-complete")) revealComplete(root);
      }, 8000);

      let resizeTimer = null;
      window.addEventListener(
        "resize",
        () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            align(root);
            if (root.classList.contains("is-complete") || reduceMotion) revealComplete(root);
          }, 100);
        },
        { passive: true },
      );
    }

    roots.forEach((root) => {
      boot(root).catch(() => {
        root.classList.add("is-fallback");
        root.classList.remove("is-booting");
        cueHeroSubtitle();
        document.dispatchEvent(
          new CustomEvent("heathers:wordmark-complete", { detail: { root } }),
        );
      });
    });
  })();

  (function initHeroSubtitleAfterWordmark() {
    const lines = document.querySelectorAll(".hero__subtitle--after-wordmark");
    if (!lines.length) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function show() {
      lines.forEach((el) => el.classList.add("is-in"));
    }

    if (reduceMotion) {
      show();
      return;
    }

    const wordmark = document.querySelector("[data-heathers-wordmark-reveal]");
    if (
      !wordmark ||
      wordmark.classList.contains("is-complete") ||
      wordmark.classList.contains("is-fallback")
    ) {
      show();
      return;
    }

    document.addEventListener("heathers:hero-subtitle", show, { once: true });
    document.addEventListener("heathers:wordmark-complete", show, { once: true });
    setTimeout(show, 8500);
  })();

  // Desktop: early-release featured collection on scroll (CSS holds until wordmark ends).
  (function initWordmarkDeferredSections() {
    const wordmark = document.querySelector("[data-heathers-wordmark-reveal]");
    const target = document.querySelector("[data-defer-until-wordmark]");
    if (!wordmark || !target) return;

    const desktopMq = window.matchMedia("(min-width: 1024px)");
    const reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    let released = false;
    let scrollHandler = null;
    let safetyTimer = null;

    function cleanup() {
      if (scrollHandler) {
        window.removeEventListener("scroll", scrollHandler);
        scrollHandler = null;
      }
      if (safetyTimer) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
      document.removeEventListener("heathers:wordmark-complete", release);
    }

    function release() {
      if (released) return;
      released = true;
      cleanup();
      target.classList.add("is-released");
    }

    function wordmarkDone() {
      return (
        wordmark.classList.contains("is-complete") ||
        wordmark.classList.contains("is-fallback")
      );
    }

    function bind() {
      released = false;
      target.classList.remove("is-released");

      if (!desktopMq.matches || reduceMq.matches || wordmarkDone()) {
        release();
        return;
      }

      document.addEventListener("heathers:wordmark-complete", release);

      scrollHandler = () => {
        if (window.scrollY > 48) release();
      };
      window.addEventListener("scroll", scrollHandler, { passive: true });
      if (window.scrollY > 48) {
        release();
        return;
      }

      safetyTimer = setTimeout(release, 10000);
    }

    bind();
    desktopMq.addEventListener("change", () => {
      cleanup();
      bind();
    });
    reduceMq.addEventListener("change", () => {
      cleanup();
      bind();
    });
  })();
})();
